import React, { useState } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { submitClaim, uploadReimbursementFile } from '../../../Services/APIs/reimbursement';
import { FileField, TextField, ReviewField, ReviewFile } from './formFields';
import { acceptFor, formatInr, toothLabel, statusLabel } from './reimbursementStatus';

const FILE_FIELDS = ['post_treatment_photo', 'invoice_receipt'];

const emptyFields = () => ({
    clinic_name: '',
    clinic_address: '',
    procedure_performed: '',
    treatment_completion_date: '',
    total_cost: '',
    payout_method: 'upi',
    bank_account_number: '',
    bank_ifsc: '',
    bank_account_holder_name: '',
    upi_id: '',
});

const TOTAL_STEPS = 5;

// Proof of Treatment for an approved Reimbursement: what was done, what it cost, a post-treatment
// photo and the invoice, and where to pay the member back. Native 5-page replica of the
// dental360.toothlens.com/claims form (ADR-0002), with a final review step before submit. Only
// reachable while the Reimbursement is in Reimbursement Treatment Pending — enforced server-side.
// Re-uploads a reviewer asks for later go through ReuploadForm, not this form.
// `initialInvoice` ({ file, url }) is the invoice the member already uploaded from the status card.
const ClaimForm = ({ prescriptionId, approvedProcedures = [], initialInvoice = null, onClose, onSuccess }) => {
    const [step, setStep] = useState(1);
    const [fields, setFields] = useState(() => emptyFields());
    const [uploads, setUploads] = useState(() => (initialInvoice?.url
        ? { invoice_receipt: { file: initialInvoice.file, status: 'done', url: initialInvoice.url, error: null } }
        : {}));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [isDone, setIsDone] = useState(false);
    const [declarationAccepted, setDeclarationAccepted] = useState(false);

    const setField = (name, value) => setFields((prev) => ({ ...prev, [name]: value }));

    const setFile = async (name, file) => {
        if (!file) {
            setUploads((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
            return;
        }
        setUploads((prev) => ({ ...prev, [name]: { file, status: 'uploading', url: null, error: null } }));
        try {
            const response = await uploadReimbursementFile(file, { type: 'claim', field: name });
            setUploads((prev) => ({ ...prev, [name]: { file, status: 'done', url: response.data?.data?.url, error: null } }));
        } catch (err) {
            const message = err?.response?.data?.message || 'Upload failed. Please try again.';
            setUploads((prev) => ({ ...prev, [name]: { file, status: 'error', url: null, error: message } }));
        }
    };

    const isAnyUploading = FILE_FIELDS.some((name) => uploads[name]?.status === 'uploading');

    const goNext = (e) => {
        e.preventDefault();
        setError('');

        if (step === 2) {
            if (!fields.procedure_performed.trim() || !fields.treatment_completion_date || !fields.total_cost) {
                setError('Procedure, completion date, and total cost are required.');
                return;
            }
        }
        if (step === 3) {
            if (isAnyUploading) {
                setError('Please wait for uploads to finish.');
                return;
            }
            if (!uploads.post_treatment_photo?.url || !uploads.invoice_receipt?.url) {
                setError('Please upload the post-treatment photo and invoice/receipt.');
                return;
            }
        }
        setStep((s) => Math.min(s + 1, TOTAL_STEPS));
    };

    const goBack = () => setStep((s) => Math.max(s - 1, 1));

    const handleSubmit = async () => {
        if (!declarationAccepted) {
            setError('Please accept the declaration to submit.');
            return;
        }
        setIsSubmitting(true);
        setError('');
        try {
            const payload = {
                prescription_id: prescriptionId || '',
                ...fields,
                declaration_accepted: 'true',
            };
            if (uploads.post_treatment_photo?.url) {
                payload.post_treatment_photo_url = uploads.post_treatment_photo.url;
                payload.post_treatment_photo_name = uploads.post_treatment_photo.file?.name;
            }
            if (uploads.invoice_receipt?.url) {
                payload.invoice_receipt_url = uploads.invoice_receipt.url;
                payload.invoice_receipt_name = uploads.invoice_receipt.file?.name;
            }

            await submitClaim(payload);
            setIsDone(true);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to submit. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isDone) {
        return (
            <div className="p-8 flex flex-col items-center text-center" data-testid="proof-submitted">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-gray-800 font-bold text-lg mb-1">Proof of treatment uploaded ✓</h3>
                <p className="text-gray-500 text-sm mb-3">Your proof has been submitted successfully. Our team will review your treatment details next.</p>
                <p className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-3 py-1 mb-6">
                    Status: {statusLabel('reimbursement_treatment_review')}
                </p>
                <button
                    onClick={onSuccess}
                    className="w-full py-3 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors"
                >
                    Done
                </button>
            </div>
        );
    }

    return (
        <>
            {/* Header */}
            <div className="bg-purple-600 p-5 rounded-t-2xl flex justify-between items-center flex-shrink-0">
                <div>
                    <h2 className="text-lg font-bold text-white">Upload Proof of Treatment</h2>
                    <p className="text-purple-100 text-xs mt-1">Reimbursement treatment · Step {step} of {TOTAL_STEPS}</p>
                </div>
                <button
                    onClick={onClose}
                    className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
                >
                    <X className="w-4 h-4 text-white" />
                </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1">
                {error && (
                    <p className="text-red-600 text-sm bg-red-50 border border-red-100 rounded-lg p-3 mb-4">{error}</p>
                )}

                {step === 1 && (
                    <form onSubmit={goNext} className="space-y-4">
                        {/* The patient is the one named on the approved prescription. */}
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Clinic details</p>
                        <TextField label="Clinic name" value={fields.clinic_name} onChange={(e) => setField('clinic_name', e.target.value)} />
                        <TextField label="Clinic address" value={fields.clinic_address} onChange={(e) => setField('clinic_address', e.target.value)} />

                        <button
                            type="submit"
                            className="w-full py-3 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors flex items-center justify-center gap-2"
                        >
                            Next <ArrowRight className="w-4 h-4" />
                        </button>
                    </form>
                )}

                {step === 2 && (
                    <form onSubmit={goNext} className="space-y-4">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Treatment details</p>
                        <TextField label="Procedure performed" required value={fields.procedure_performed} onChange={(e) => setField('procedure_performed', e.target.value)} />
                        <TextField label="Treatment completion date" type="date" required value={fields.treatment_completion_date} onChange={(e) => setField('treatment_completion_date', e.target.value)} />
                        <TextField label="Total cost (₹)" type="number" min="0" required value={fields.total_cost} onChange={(e) => setField('total_cost', e.target.value)} />
                        <p className="text-xs text-gray-400">This proof of treatment is linked to your approved reimbursement prescription automatically.</p>
                        {approvedProcedures.length > 0 && (
                            <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-xs" data-testid="claim-approved-procedures">
                                <p className="font-semibold text-green-800 mb-1">Approved for reimbursement</p>
                                <ul className="space-y-0.5 text-green-900">
                                    {approvedProcedures.map((p) => (
                                        <li key={p._id} className="flex justify-between gap-2">
                                            <span>{p.treatment} · {toothLabel(p.tooth)}</span>
                                            <span className="font-semibold">{formatInr(p.approved_amount)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button type="button" onClick={goBack} className="flex-1 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl transition-colors flex items-center justify-center gap-2">
                                <ArrowLeft className="w-4 h-4" /> Back
                            </button>
                            <button type="submit" className="flex-1 py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors flex items-center justify-center gap-2">
                                Next <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </form>
                )}

                {step === 3 && (
                    <form onSubmit={goNext} className="space-y-4">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Supporting documents</p>
                        <FileField label="Post-treatment photo" required accept={acceptFor('post_treatment_photo')} testId="upload-post_treatment_photo" file={uploads.post_treatment_photo?.file} uploading={uploads.post_treatment_photo?.status === 'uploading'} error={uploads.post_treatment_photo?.error} onChange={(f) => setFile('post_treatment_photo', f)} />
                        <FileField label="Invoice / receipt" required accept={acceptFor('invoice_receipt')} testId="upload-invoice_receipt" file={uploads.invoice_receipt?.file} uploading={uploads.invoice_receipt?.status === 'uploading'} error={uploads.invoice_receipt?.error} onChange={(f) => setFile('invoice_receipt', f)} />

                        <div className="flex gap-3">
                            <button type="button" onClick={goBack} className="flex-1 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl transition-colors flex items-center justify-center gap-2">
                                <ArrowLeft className="w-4 h-4" /> Back
                            </button>
                            <button type="submit" disabled={isAnyUploading} className="flex-1 py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                                {isAnyUploading ? 'Uploading…' : <>Next <ArrowRight className="w-4 h-4" /></>}
                            </button>
                        </div>
                    </form>
                )}

                {step === 4 && (
                    <form onSubmit={goNext} className="space-y-4">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Payout details</p>
                        <p className="text-xs text-gray-400 -mt-2">Used only for the back-office team's manual payout — no payment is processed in-app.</p>

                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setField('payout_method', 'upi')}
                                className={`flex-1 py-2 text-sm font-semibold rounded-lg border ${fields.payout_method === 'upi' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-200'}`}
                            >
                                UPI
                            </button>
                            <button
                                type="button"
                                onClick={() => setField('payout_method', 'bank')}
                                className={`flex-1 py-2 text-sm font-semibold rounded-lg border ${fields.payout_method === 'bank' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-200'}`}
                            >
                                Bank Account
                            </button>
                        </div>

                        {fields.payout_method === 'upi' ? (
                            <TextField label="UPI ID" value={fields.upi_id} onChange={(e) => setField('upi_id', e.target.value)} />
                        ) : (
                            <>
                                <TextField label="Account holder name" value={fields.bank_account_holder_name} onChange={(e) => setField('bank_account_holder_name', e.target.value)} />
                                <TextField label="Account number" value={fields.bank_account_number} onChange={(e) => setField('bank_account_number', e.target.value)} />
                                <TextField label="IFSC code" value={fields.bank_ifsc} onChange={(e) => setField('bank_ifsc', e.target.value)} />
                            </>
                        )}

                        <div className="flex gap-3">
                            <button type="button" onClick={goBack} className="flex-1 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl transition-colors flex items-center justify-center gap-2">
                                <ArrowLeft className="w-4 h-4" /> Back
                            </button>
                            <button type="submit" className="flex-1 py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors flex items-center justify-center gap-2">
                                Review &amp; Continue <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </form>
                )}

                {step === 5 && (
                    <div className="space-y-4">
                        <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                            <div>
                                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Clinic details</p>
                                <div className="space-y-1.5 text-sm">
                                    <ReviewField label="Clinic name" value={fields.clinic_name} />
                                    <ReviewField label="Clinic address" value={fields.clinic_address} />
                                </div>
                            </div>

                            <div>
                                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Treatment details</p>
                                <div className="space-y-1.5 text-sm">
                                    <ReviewField label="Procedure performed" value={fields.procedure_performed} />
                                    <ReviewField label="Completion date" value={fields.treatment_completion_date} />
                                    <ReviewField label="Total cost" value={fields.total_cost ? `₹${fields.total_cost}` : ''} />
                                </div>
                            </div>

                            <div className="space-y-3">
                                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Uploaded files</p>
                                <ReviewFile label="Post-treatment photo" file={uploads.post_treatment_photo?.file} />
                                <ReviewFile label="Invoice / receipt" file={uploads.invoice_receipt?.file} />
                            </div>

                            <div>
                                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Payout details</p>
                                <div className="space-y-1.5 text-sm">
                                    <ReviewField label="Method" value={fields.payout_method === 'upi' ? 'UPI' : 'Bank Account'} />
                                    {fields.payout_method === 'upi' ? (
                                        <ReviewField label="UPI ID" value={fields.upi_id} />
                                    ) : (
                                        <>
                                            <ReviewField label="Account holder name" value={fields.bank_account_holder_name} />
                                            <ReviewField label="Account number" value={fields.bank_account_number} />
                                            <ReviewField label="IFSC code" value={fields.bank_ifsc} />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        <label className="flex items-start gap-2 text-xs text-gray-600">
                            <input
                                type="checkbox"
                                checked={declarationAccepted}
                                data-testid="claim-declaration"
                                onChange={(e) => setDeclarationAccepted(e.target.checked)}
                                className="mt-0.5"
                            />
                            I declare that the treatment and costs stated above are accurate and were paid by me directly to the clinic.
                        </label>

                        <div className="flex gap-3">
                            <button
                                onClick={goBack}
                                disabled={isSubmitting}
                                className="flex-1 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl transition-colors flex items-center justify-center gap-2"
                            >
                                <ArrowLeft className="w-4 h-4" /> Back
                            </button>
                            <button
                                data-testid="claim-submit"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className="flex-1 py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors disabled:opacity-50"
                            >
                                {isSubmitting ? 'Submitting…' : 'Submit'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default ClaimForm;
